import React, {useEffect, useState} from 'react';
import {Link, useParams} from 'react-router-dom';
import {useAppDispatch} from '../../hooks/redux';
import {appSlice} from '../../store/reducers/AppSlice';
import {statisticModelApi} from '../../api/StatisticModelsService';
import {categoryApi} from '../../api/CategoriesService';
import Loader from '../../components/ui/Loader';
import StatsResult from '../../components/stats/StatsResult';
import StatsGroupsTable, {StatsTransactionsToggle} from '../../components/stats/StatsGroupsTable';
import StatsGroupsCharts from '../../components/stats/StatsGroupsCharts';
import SimpleDatePicker from '../../components/ui/date_picker/SimpleDatePicker';
import CurrencyPicker from '../../components/ui/account/CurrencyPicker';
import {ICurrency} from '../../model/ICurrency';
import {IStatisticModelRunReq} from '../../model/IStats';
import {aggregationLabel, MONTH_OPTIONS, periodTypeLabel} from '../../utils/statsModels';
import {TransactionType} from '../../model/TransactionType';
import * as common from '../../utils/common';

const StatsModelRunPage = () => {
    const {id} = useParams();
    const modelId = Number(id);
    const dispatch = useAppDispatch();
    const {setTitle} = appSlice.actions;
    const {data: model, isLoading} = statisticModelApi.useFetchStatisticModelQuery(modelId);
    const {data: currencies} = categoryApi.useFetchAllCurrenciesQuery();
    const [runModel, {data: result, isLoading: isRunning}] = statisticModelApi.useRunStatisticModelMutation();

    const now = new Date();
    const [year, setYear] = useState<number>(now.getFullYear());
    const [month, setMonth] = useState<number>(now.getMonth() + 1);
    const [nMonths, setNMonths] = useState<number>(3);
    const [dateFrom, setDateFrom] = useState(common.getFirstDayOfCurrentMonthDate());
    const [dateTo, setDateTo] = useState(common.trunc(new Date()));
    const [currency, setCurrency] = useState<ICurrency | null>(null);

    useEffect(() => {
        dispatch(setTitle(model?.name ?? 'Run stats model'));
    }, [dispatch, model, setTitle]);

    useEffect(() => {
        if (!model) {
            return;
        }
        if (model.default_year) {
            setYear(model.default_year);
        }
        if (model.default_month) {
            setMonth(model.default_month);
        }
        if (model.default_n_months) {
            setNMonths(model.default_n_months);
        }
        if (model.default_date_from) {
            setDateFrom(model.default_date_from);
        }
        if (model.default_date_to) {
            setDateTo(model.default_date_to);
        }
        if (model.currency) {
            setCurrency(model.currency);
        }
    }, [model]);

    useEffect(() => {
        if (!currency && currencies?.length) {
            setCurrency(currencies[0]);
        }
    }, [currency, currencies]);

    const buildRunBody = (): IStatisticModelRunReq => {
        if (!model) {
            return {};
        }
        const body: IStatisticModelRunReq = {};
        if (model.period_type === 'month') {
            body.year = year;
            body.month = month;
        } else if (model.period_type === 'last_n_months') {
            body.n_months = nMonths;
        } else {
            body.date_from = dateFrom;
            body.date_to = dateTo;
        }
        if (model.currency_mode === 'parameterized' && currency) {
            body.currency = currency.id;
        }
        return body;
    };

    const handleEvaluate = () => {
        if (!model) {
            return;
        }
        runModel({id: model.id, body: buildRunBody()});
    };

    if (isLoading) {
        return <div className="relative min-h-[8rem]"><Loader/></div>;
    }

    if (!model) {
        return <p className="text-sm text-gray-500">Stats model not found.</p>;
    }

    return (
        <div>
            <div className="mb-4 flex items-start justify-between gap-4">
                <div>
                    <p className="text-sm text-gray-500">
                        {model.transaction_type === TransactionType.Income ? 'Income' : 'Expense'}
                        {' · '}
                        {periodTypeLabel(model.period_type)}
                        {' · '}
                        {aggregationLabel(model)}
                    </p>
                </div>
                <Link
                    to={`/stats/models/${model.id}/edit`}
                    className="py-2 px-4 text-sm font-medium rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                    Edit
                </Link>
            </div>

            <div className="mb-4 pb-4 border-b border-gray-300">
                <div className="flex flex-row items-end flex-wrap gap-4">
                    {model.period_type === 'month' && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700" htmlFor="run-year">Year</label>
                                <input
                                    id="run-year"
                                    type="number"
                                    className="mt-1 bg-white border border-gray-300 rounded-md shadow-sm sm:text-sm"
                                    value={year}
                                    onChange={(event) => setYear(Number(event.target.value))}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700" htmlFor="run-month">Month</label>
                                <select
                                    id="run-month"
                                    className="mt-1 bg-white border border-gray-300 rounded-md shadow-sm sm:text-sm"
                                    value={month}
                                    onChange={(event) => setMonth(Number(event.target.value))}
                                >
                                    {MONTH_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                    {model.period_type === 'last_n_months' && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700" htmlFor="run-n-months">Last N months</label>
                            <input
                                id="run-n-months"
                                type="number"
                                min={1}
                                className="mt-1 bg-white border border-gray-300 rounded-md shadow-sm sm:text-sm"
                                value={nMonths}
                                onChange={(event) => setNMonths(Number(event.target.value))}
                            />
                        </div>
                    )}
                    {model.period_type === 'date_range' && (
                        <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Date from</label>
                                <SimpleDatePicker date={dateFrom} setDate={setDateFrom} className="mt-1"/>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Date to</label>
                                <SimpleDatePicker date={dateTo} setDate={setDateTo} className="mt-1"/>
                            </div>
                        </>
                    )}
                    {model.currency_mode === 'parameterized' && currencies && (
                        <CurrencyPicker
                            label="Currency"
                            currency={currency}
                            setCurrency={setCurrency}
                            currencies={currencies}
                            smallerSize
                        />
                    )}
                    <button
                        type="button"
                        className="py-2 px-6 border border-transparent text-sm font-medium rounded-md bg-green-100 text-green-800 hover:bg-green-200 focus:outline-none"
                        onClick={handleEvaluate}
                    >
                        Evaluate
                    </button>
                </div>
            </div>

            {isRunning && <div className="relative min-h-[8rem]"><Loader/></div>}
            {result && !isRunning && (
                <>
                    <StatsResult number_of_transactions={result.number_of_transactions} total_amount={result.totals}/>
                    <StatsGroupsCharts
                        groups={result.groups}
                        preferredCurrency={model.currency_mode === 'multi' ? null : (currency || model.currency)}
                    />
                    {result.groups.length > 0 ? (
                        <StatsGroupsTable
                            groups={result.groups}
                            modelId={model.id}
                            runBody={buildRunBody()}
                            trType={model.transaction_type}
                        />
                    ) : (
                        <div className="mt-4">
                            <StatsTransactionsToggle
                                modelId={model.id}
                                body={buildRunBody()}
                                trType={model.transaction_type}
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default StatsModelRunPage;
